import { useEffect, useState } from 'react';
import { cn } from '../lib/cn';
import { Icon, type IconName } from './Icon';

export type BrandImgShape = 'circle' | 'rounded' | 'square';

export interface BrandImgProps {
  /** Шлях із packages/shared/src/brand.ts. Файлу може ще не існувати. */
  src: string;
  alt: string;
  /** Фірмова іконка для фолбеку. Емодзі тут не використовуються. */
  fallbackIcon?: IconName;
  /** Два кольори градієнта фолбеку. За замовчуванням — фірмові фіолет/золото. */
  colors?: readonly [string, string];
  shape?: BrandImgShape;
  className?: string;
  /** Класи для іконки у фолбеку (розмір/зсув). */
  fallbackClassName?: string;
  eager?: boolean;
  /** Декоративна картинка — прибираємо з дерева доступності. */
  decorative?: boolean;
}

const SHAPE_CLASS: Record<BrandImgShape, string> = {
  circle: 'rounded-full',
  rounded: 'rounded-[var(--lk-radius-lg)]',
  square: 'rounded-none',
};

/**
 * Картинка бренд-паку з елегантним фолбеком.
 *
 * Ілюстровані маскоти в /public/brand/heroes ще не згенеровані — їх дає
 * Gemini за промтами з docs/GEMINI_PROMPTS.md. Доти кожен такий слот
 * показує градієнтну плитку з ФІРМОВОЮ ІКОНКОЮ (не емодзі), тому застосунок
 * виглядає цілісним уже зараз, а не «після того, як домалюють».
 */
export function BrandImg({
  src,
  alt,
  fallbackIcon,
  colors,
  shape = 'rounded',
  className,
  fallbackClassName,
  eager = false,
  decorative = false,
}: BrandImgProps): React.JSX.Element {
  const [failed, setFailed] = useState(false);
  const [loaded, setLoaded] = useState(false);

  // Зміна src (інший ранг, інший маскот) скидає стан.
  useEffect(() => {
    setFailed(false);
    setLoaded(false);
  }, [src]);

  const [from, to] = colors ?? (['#7C5CFF', '#F038C8'] as const);

  if (failed) {
    return (
      <span
        className={cn(
          'relative grid place-items-center overflow-hidden select-none',
          SHAPE_CLASS[shape],
          className,
        )}
        style={{
          background: `radial-gradient(120% 120% at 30% 10%, ${from} 0%, ${to} 70%, rgba(0,0,0,.55) 100%)`,
          boxShadow: 'inset 0 2px 0 rgba(255,255,255,.35), inset 0 -8px 18px rgba(0,0,0,.35)',
          border: '2px solid rgba(255,255,255,.18)',
        }}
        role={decorative ? 'presentation' : 'img'}
        aria-label={decorative ? undefined : alt}
        aria-hidden={decorative || undefined}
        data-brand-fallback="true"
      >
        <Icon
          name={fallbackIcon ?? 'star'}
          className={cn('h-[58%] w-[58%] drop-shadow-[0_3px_0_rgba(0,0,0,.45)]', fallbackClassName)}
        />
      </span>
    );
  }

  return (
    <span
      className={cn('relative block overflow-hidden', SHAPE_CLASS[shape], className)}
      style={
        loaded
          ? undefined
          : {
              // Поки картинка вантажиться — тихий градієнт замість білої діри.
              background: `linear-gradient(160deg, ${from}33, ${to}22)`,
            }
      }
    >
      <img
        src={src}
        alt={decorative ? '' : alt}
        aria-hidden={decorative || undefined}
        loading={eager ? 'eager' : 'lazy'}
        decoding="async"
        draggable={false}
        onError={() => setFailed(true)}
        onLoad={() => setLoaded(true)}
        className={cn(
          'h-full w-full object-contain transition-opacity duration-300',
          loaded ? 'opacity-100' : 'opacity-0',
        )}
      />
    </span>
  );
}
